<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="ask-question-modal-container">
            <div class="modal-header-custom">
                <h5 class="modal-title-custom">Ask a Question</h5>
                <button @click="closeModal" class="close-button-custom">&times;</button>
            </div>

            <div v-if="!isSubmitted" class="modal-body-custom">
                <p class="modal-description">Have a question about this contract? Type it below and we'll get back to you shortly.</p>
                <div class="form-group">
                    <textarea 
                        v-model="questionText" 
                        class="form-control question-textarea" 
                        placeholder="Type your question here..."
                        rows="5"
                    ></textarea>
                </div>
                <p v-if="questionError" class="error-message">{{ questionError }}</p>
            </div>

            <div v-else class="modal-body-custom thank-you-message">
                <h5 class="thank-you-title">Thank you!</h5>
                <p>Your query has been received. We will be in touch shortly.</p>
            </div>

            <div class="modal-footer-custom">
                <button 
                    v-if="!isSubmitted"
                    @click="submitQuestion" 
                    class="submit-button" 
                    :disabled="isSubmitting || !questionText.trim()"
                >
                    {{ isSubmitting ? 'Sending...' : 'Submit Question' }}
                </button>
                <button 
                    v-else
                    @click="closeModal"
                    class="close-button"
                >
                    Close
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AskQuestionModal',
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        contractUniqueKey: {
            type: String,
            required: true,
        },
        contractAccessKey: {
            type: String,
            required: true,
        },
        clientEmail: {
            type: String,
            required: true,
        }
    },
    data() {
        return {
            questionText: '',
            questionError: '',
            isSubmitting: false,
            isSubmitted: false,
        };
    },
    watch: {
        show(newVal) {
            if (newVal) {
                // Reset state when the modal is opened
                this.questionText = '';
                this.questionError = '';
                this.isSubmitting = false;
                this.isSubmitted = false;
            }
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        async submitQuestion() {
            this.questionError = '';
            if (!this.questionText.trim()) {
                this.questionError = 'Please enter your question before submitting.';
                return;
            }

            this.isSubmitting = true;

            const payload = {
                unique_key: this.contractUniqueKey,
                access_key: this.contractAccessKey,
                email: this.clientEmail,
                question: this.questionText.trim(),
            };

            //console.log(this.contractAccessKey)

            try {
                // This assumes you have an API endpoint to handle the question submission.
                // You'll need to replace '/contracts/ask-question' with your actual endpoint.
                const response = await this.$axios_internal.post('/contracts/ask-question', payload);
                if (response.data.success) {
                    this.isSubmitted = true;
                    this.$emit('question-submitted');
                } else {
                    this.questionError = response.data.message || 'An unknown error occurred.';
                    this.$emit('question-failed', this.questionError);
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to submit question. Please try again.';
                this.questionError = errorMessage;
                this.$emit('question-failed', errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
/* You can reuse the existing modal styles from ShareContractModal.vue */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
}

.ask-question-modal-container {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 550px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;
}

.modal-title-custom {
    font-size: 1.25rem;
    font-weight: 500;
    margin: 0;
}

.close-button-custom {
    background: none;
    border: none;
    font-size: 1.75rem;
    font-weight: bold;
    line-height: 1;
    color: #6c757d;
    cursor: pointer;
    opacity: 0.7;
}

.close-button-custom:hover {
    opacity: 1;
}

.modal-body-custom {
    padding: 1.5rem;
    overflow-y: auto;
    flex-grow: 1;
}

.modal-description {
    color: #555;
    margin-bottom: 1rem;
    line-height: 1.5;
}

.form-group {
    margin-bottom: 1rem;
}

.question-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ced4da;
    border-radius: 8px;
    font-size: 14px;
    resize: vertical;
}

.question-textarea:focus {
    outline: none;
    border-color: #00a0c1;
    box-shadow: 0 0 0 2px rgba(0, 160, 193, 0.25);
}

.error-message {
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: -0.5rem;
    margin-bottom: 0;
}

.modal-footer-custom {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e9ecef;
    display: flex;
    justify-content: flex-end;
    background-color: #f8f9fa;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

.submit-button, .close-button {
    background-color: #00a0c1;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
}

.submit-button:hover, .close-button:hover {
    background-color: #008da9;
}

.submit-button:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
}

.thank-you-message {
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.thank-you-title {
    font-size: 1.5rem;
    color: #28a745;
    margin-bottom: 0.5rem;
}
</style>
